import { useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import StateIcon from "./StateIcon";
import SectionHeading from "./SectionHeading";
import { STATES } from "@/data/states";


function StateCard({ state }: { state: { slug: string; name: string; abbr: string } }) {
  return (
    <Link
      to={`/browse/${state.slug}`}
      className="group flex flex-col items-center gap-2 p-5 rounded-2xl border border-white/10 bg-gradient-to-b from-[#0f2531] to-[#07121b] hover:-translate-y-1.5 hover:border-primary/30 transition-all duration-300 min-w-[155px] shrink-0"
    >
      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/20 to-accent/10 border border-white/10 flex items-center justify-center text-primary group-hover:scale-105 transition-transform">
        <StateIcon abbr={state.abbr} className="w-10 h-10" />
      </div>
      <span className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors text-center whitespace-nowrap">
        {state.name}
      </span>
      <span className="text-[11px] font-bold tracking-wider text-teal-300">{state.abbr}</span>
    </Link>
  );
}

export default function CategoriesSection() {
  const scrollRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: "left" | "right") => {
    if (!scrollRef.current) return;
    const amount = 400;
    scrollRef.current.scrollBy({
      left: direction === "left" ? -amount : amount,
      behavior: "smooth",
    });
  };

  return (
    <section className="py-16 lg:py-24">
      <div className="container mx-auto px-4 lg:px-8">
        <SectionHeading
          eyebrow="Browse by State"
          title={
            <>
              Browse by <span className="text-gradient-aqua">State</span>
            </>
          }
          description="Find top-rated med spas and aesthetic providers across the United States"
          action={
            <div className="hidden sm:flex items-center gap-2">
              <button
                onClick={() => scroll("left")}
                className="p-2.5 rounded-xl border border-white/10 bg-white/5 text-muted-foreground hover:text-foreground hover:border-primary/30 transition-colors"
                aria-label="Scroll left"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => scroll("right")}
                className="p-2.5 rounded-xl border border-white/10 bg-white/5 text-muted-foreground hover:text-foreground hover:border-primary/30 transition-colors"
                aria-label="Scroll right"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          }
        />

        <div
          ref={scrollRef}
          className="flex gap-3 overflow-x-auto scrollbar-hide pt-2 pb-2 snap-x snap-mandatory"
        >
          {STATES.map((state) => (
            <div key={state.slug} className="snap-start">
              <StateCard state={state} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
